import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { EvalLayout } from '@/components/eval/EvalLayout';
import { ExecuteFromSetModal } from '@/components/eval/ExecuteFromSetModal';
import { VoteComparisonModal } from '@/components/eval/VoteComparisonModal';
import { StarIcon } from '@/components/icons/common';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { SourceBadge } from '@/components/ui/SourceBadge';
import { type BadgeVariant, StatusBadge } from '@/components/ui/StatusBadge';
import {
  Table,
  TableBody,
  TableCell,
  TableEmptyState,
  TableHead,
  TableHeaderCell,
  TableRow,
} from '@/components/ui/Table';
import { apiRequest } from '@/config';
import { logError } from '@/lib/logger';

// Types
interface VoterSummary {
  userEmail: string;
  voteCount: number;
  lastVotedAt: string;
}

interface DetailedPrompt {
  id: string;
  promptText: string;
  category: string | null;
  createdAt: string;
  lastResultAt: string | null;
  status: string;
  source: 'seed' | 'arena';
  validResultsCount: number;
  totalResultsCount: number;
  totalVotes: number;
  voters: VoterSummary[];
  featuredOnHome: boolean;
  currentUserHasVoted: boolean;
}

interface PromptSet {
  id: string;
  name: string;
  description: string | null;
  created_at: string;
  prompt_count: number;
}

interface Pagination {
  total: number;
  limit: number;
  offset: number;
  page: number;
  totalPages: number;
}

// Vote lookup types
interface VoteLookupResult {
  id: string;
  arena_run_id: string;
  result_a_id: string;
  result_b_id: string;
  config_a_id: string;
  config_b_id: string;
  config_a_name: string;
  config_b_name: string;
  choice: 'a' | 'b' | 'tie' | 'both_bad';
  user_email?: string;
  created_at: string;
  prompt_text: string;
  prompt_id: string | null;
  category: string | null;
  source: 'seed' | 'arena' | null;
}

type VoteFilter = 'all' | 'voted' | 'unvoted';
type DateFilter = 'all' | '7days' | '30days';
type SourceFilter = 'all' | 'seed' | 'arena';
type CategoryFilter = string; // 'all' or specific category name

const PAGE_SIZE = 50;

// Utility functions
function formatEmail(email: string): string {
  return email.split('@')[0];
}

function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function getStatusVariant(status: string): BadgeVariant {
  const variants: Record<string, BadgeVariant> = {
    completed: 'success',
    running: 'info',
    pending: 'warning',
    failed: 'error',
  };
  return variants[status] || 'neutral';
}

// Sub-components
function StatsCard({
  label,
  value,
  color,
}: {
  label: string;
  value: number | string;
  color?: string;
}) {
  return (
    <div
      style={{
        background: '#FFFFFF',
        border: '1px solid #E8E0D8',
        borderRadius: '12px',
        padding: '1.5rem',
      }}
    >
      <div
        style={{
          fontSize: '0.8125rem',
          color: '#6B6B6B',
          marginBottom: '0.5rem',
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: '1.5rem',
          fontWeight: 600,
          color: color || '#1A1A1A',
        }}
      >
        {value}
      </div>
    </div>
  );
}

function VoterBadges({
  voters,
  maxDisplay = 3,
}: {
  voters: VoterSummary[];
  maxDisplay?: number;
}) {
  if (voters.length === 0) {
    return (
      <span style={{ fontSize: '0.8125rem', color: '#A0A0A0' }}>
        No votes yet
      </span>
    );
  }

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
      {voters.slice(0, maxDisplay).map(voter => (
        <span
          key={voter.userEmail}
          title={`${voter.userEmail}: ${voter.voteCount} votes, last ${formatDate(voter.lastVotedAt)}`}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            padding: '2px 8px',
            borderRadius: '12px',
            fontSize: '0.75rem',
            background: '#F5F0EB',
            color: '#2D2D2D',
          }}
        >
          {formatEmail(voter.userEmail)}
          <span
            style={{
              background: '#8B2332',
              color: 'white',
              padding: '0 4px',
              borderRadius: '8px',
              fontSize: '0.625rem',
              fontWeight: 600,
            }}
          >
            {voter.voteCount}
          </span>
        </span>
      ))}
      {voters.length > maxDisplay && (
        <span
          style={{
            fontSize: '0.75rem',
            color: '#6B6B6B',
            alignSelf: 'center',
          }}
        >
          +{voters.length - maxDisplay} more
        </span>
      )}
    </div>
  );
}

interface FilterBarProps {
  sourceFilter: SourceFilter;
  voteFilter: VoteFilter;
  dateFilter: DateFilter;
  hideNoResults: boolean;
  promptSetFilter: string;
  promptSets: PromptSet[];
  categoryFilter: CategoryFilter;
  categories: string[];
  onSourceFilterChange: (filter: SourceFilter) => void;
  onVoteFilterChange: (filter: VoteFilter) => void;
  onDateFilterChange: (filter: DateFilter) => void;
  onHideNoResultsToggle: () => void;
  onPromptSetFilterChange: (setId: string) => void;
  onCategoryFilterChange: (category: CategoryFilter) => void;
}

function FilterBar({
  sourceFilter,
  voteFilter,
  dateFilter,
  hideNoResults,
  promptSetFilter,
  promptSets,
  categoryFilter,
  categories,
  onSourceFilterChange,
  onVoteFilterChange,
  onDateFilterChange,
  onHideNoResultsToggle,
  onPromptSetFilterChange,
  onCategoryFilterChange,
}: FilterBarProps) {
  const buttonBase = {
    padding: '0.5rem 1rem',
    border: '1px solid #E8E0D8',
    borderRadius: '6px',
    fontSize: '0.875rem',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
  };

  const activeStyle = {
    ...buttonBase,
    background: '#8B2332',
    color: 'white',
    fontWeight: 600,
    borderColor: '#8B2332',
  };

  const inactiveStyle = {
    ...buttonBase,
    background: 'white',
    color: '#1A1A1A',
    fontWeight: 400,
  };

  return (
    <div
      style={{
        display: 'flex',
        gap: '0.5rem',
        marginBottom: '1.5rem',
        alignItems: 'center',
        flexWrap: 'wrap',
      }}
    >
      {/* Source Filters */}
      {(['all', 'seed', 'arena'] as SourceFilter[]).map(filter => (
        <button
          key={filter}
          onClick={() => onSourceFilterChange(filter)}
          style={sourceFilter === filter ? activeStyle : inactiveStyle}
        >
          {filter === 'all' && 'All Sources'}
          {filter === 'seed' && 'Seed Only'}
          {filter === 'arena' && 'Arena Only'}
        </button>
      ))}

      <Divider />

      {/* Vote Filters */}
      {(['all', 'voted', 'unvoted'] as VoteFilter[]).map(filter => (
        <button
          key={filter}
          onClick={() => onVoteFilterChange(filter)}
          style={voteFilter === filter ? activeStyle : inactiveStyle}
        >
          {filter === 'all' && 'All Votes'}
          {filter === 'voted' && 'Has Votes'}
          {filter === 'unvoted' && 'No Votes'}
        </button>
      ))}

      <Divider />

      {/* Has Results Toggle */}
      <button
        onClick={onHideNoResultsToggle}
        style={hideNoResults ? activeStyle : inactiveStyle}
      >
        {hideNoResults ? 'Has Results' : 'All Results'}
      </button>

      <Divider />

      {/* Date Filters */}
      {(['all', '7days', '30days'] as DateFilter[]).map(filter => (
        <button
          key={filter}
          onClick={() => onDateFilterChange(filter)}
          style={dateFilter === filter ? activeStyle : inactiveStyle}
        >
          {filter === 'all' && 'All Time'}
          {filter === '7days' && 'Last 7 Days'}
          {filter === '30days' && 'Last 30 Days'}
        </button>
      ))}

      {/* Prompt Set Filter */}
      {promptSets.length > 0 && (
        <>
          <Divider />
          <select
            value={promptSetFilter}
            onChange={e => onPromptSetFilterChange(e.target.value)}
            style={{
              padding: '0.5rem 1rem',
              border: '1px solid #E8E0D8',
              borderRadius: '6px',
              background: promptSetFilter !== 'all' ? '#8B2332' : 'white',
              color: promptSetFilter !== 'all' ? 'white' : '#1A1A1A',
              cursor: 'pointer',
              fontSize: '0.875rem',
              fontWeight: promptSetFilter !== 'all' ? 600 : 400,
            }}
          >
            <option value="all">All Sets</option>
            {promptSets.map(set => (
              <option key={set.id} value={set.id}>
                {set.name} ({set.prompt_count})
              </option>
            ))}
          </select>
        </>
      )}

      {/* Category Filter */}
      {categories.length > 0 && (
        <>
          <Divider />
          <select
            value={categoryFilter}
            onChange={e => onCategoryFilterChange(e.target.value)}
            style={{
              padding: '0.5rem 1rem',
              border: '1px solid #E8E0D8',
              borderRadius: '6px',
              background: categoryFilter !== 'all' ? '#8B2332' : 'white',
              color: categoryFilter !== 'all' ? 'white' : '#1A1A1A',
              cursor: 'pointer',
              fontSize: '0.875rem',
              fontWeight: categoryFilter !== 'all' ? 600 : 400,
            }}
          >
            <option value="all">All Categories</option>
            {categories.map(cat => (
              <option key={cat} value={cat}>
                {cat}
              </option>
            ))}
          </select>
        </>
      )}
    </div>
  );
}

// Vote ID Search Bar
function VoteSearchBar({
  onVoteFound,
}: {
  onVoteFound: (vote: VoteLookupResult) => void;
}) {
  const [voteId, setVoteId] = useState('');
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSearch = async () => {
    const trimmed = voteId.trim();
    if (!trimmed) return;

    try {
      setSearching(true);
      setError(null);
      const response = await apiRequest(`/api/arena/votes/${trimmed}`);
      if (response.success && response.vote) {
        onVoteFound(response.vote);
        setVoteId('');
      } else {
        setError('Vote not found');
      }
    } catch (err: any) {
      logError('Vote lookup failed', err);
      setError(
        err.status === 404 ? 'Vote not found' : err.message || 'Lookup failed'
      );
    } finally {
      setSearching(false);
    }
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
      <div
        style={{ position: 'relative', display: 'flex', alignItems: 'center' }}
      >
        <input
          type="text"
          value={voteId}
          onChange={e => {
            setVoteId(e.target.value);
            setError(null);
          }}
          onKeyDown={e => {
            if (e.key === 'Enter') handleSearch();
          }}
          placeholder="Search by Vote ID..."
          style={{
            padding: '0.5rem 0.75rem',
            border: `1px solid ${error ? '#C41E3A' : '#E8E0D8'}`,
            borderRadius: '6px',
            fontSize: '0.875rem',
            width: '280px',
            outline: 'none',
            transition: 'border-color 0.15s ease',
            fontFamily: 'monospace',
          }}
        />
        {error && (
          <span
            style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              fontSize: '0.75rem',
              color: '#C41E3A',
              marginTop: '2px',
              whiteSpace: 'nowrap',
            }}
          >
            {error}
          </span>
        )}
      </div>
      <button
        onClick={handleSearch}
        disabled={searching || !voteId.trim()}
        style={{
          padding: '0.5rem 1rem',
          border: '1px solid #8B2332',
          borderRadius: '6px',
          background: '#8B2332',
          color: 'white',
          fontSize: '0.875rem',
          cursor: searching || !voteId.trim() ? 'not-allowed' : 'pointer',
          opacity: searching || !voteId.trim() ? 0.6 : 1,
          fontWeight: 500,
          transition: 'opacity 0.15s ease',
          whiteSpace: 'nowrap',
        }}
      >
        {searching ? 'Searching...' : 'Look Up Vote'}
      </button>
    </div>
  );
}

function Divider() {
  return (
    <div
      style={{
        width: '1px',
        height: '24px',
        background: '#E8E0D8',
        margin: '0 0.25rem',
      }}
    />
  );
}

interface PaginationControlsProps {
  pagination: Pagination;
  currentPage: number;
  onPageChange: (page: number) => void;
}

function PaginationControls({
  pagination,
  currentPage,
  onPageChange,
}: PaginationControlsProps) {
  if (pagination.totalPages <= 1) return null;

  const buttonStyle = (disabled: boolean) => ({
    padding: '0.5rem 1rem',
    border: '1px solid #E8E0D8',
    borderRadius: '6px',
    background: disabled ? '#F5F0EB' : 'white',
    color: disabled ? '#A0A0A0' : '#1A1A1A',
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontSize: '0.875rem',
  });

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginTop: '1.5rem',
        padding: '1rem',
        background: 'white',
        border: '1px solid #E8E0D8',
        borderRadius: '12px',
      }}
    >
      <div style={{ fontSize: '0.875rem', color: '#6B6B6B' }}>
        Showing {pagination.offset + 1} -{' '}
        {Math.min(pagination.offset + pagination.limit, pagination.total)} of{' '}
        {pagination.total} prompts
      </div>
      <div style={{ display: 'flex', gap: '0.5rem' }}>
        <button
          onClick={() => onPageChange(1)}
          disabled={currentPage === 1}
          style={buttonStyle(currentPage === 1)}
        >
          First
        </button>
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          style={buttonStyle(currentPage === 1)}
        >
          Previous
        </button>
        <span
          style={{
            padding: '0.5rem 1rem',
            fontSize: '0.875rem',
            color: '#1A1A1A',
            fontWeight: 500,
          }}
        >
          Page {currentPage} of {pagination.totalPages}
        </span>
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === pagination.totalPages}
          style={buttonStyle(currentPage === pagination.totalPages)}
        >
          Next
        </button>
        <button
          onClick={() => onPageChange(pagination.totalPages)}
          disabled={currentPage === pagination.totalPages}
          style={buttonStyle(currentPage === pagination.totalPages)}
        >
          Last
        </button>
      </div>
    </div>
  );
}

// Main Component
export default function UnifiedPromptsPage() {
  const [prompts, setPrompts] = useState<DetailedPrompt[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>('all');
  const [voteFilter, setVoteFilter] = useState<VoteFilter>('all');
  const [dateFilter, setDateFilter] = useState<DateFilter>('all');
  const [promptSetFilter, setPromptSetFilter] = useState<string>('all');
  const [categoryFilter, setCategoryFilter] = useState<CategoryFilter>('all');
  const [hideNoResults, setHideNoResults] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [promptSets, setPromptSets] = useState<PromptSet[]>([]);
  const CATEGORIES = [
    'Academic & Research',
    'Corporate Finance & FP&A',
    'Creative & Generative',
    'Operations & Supply Chain',
    'Professional Finance',
    'SMB & Personal',
  ];
  const [showExecuteModal, setShowExecuteModal] = useState(false);
  const [lookedUpVote, setLookedUpVote] = useState<VoteLookupResult | null>(
    null
  );

  // Helper to get date for filter
  const getDateFilterValue = (filter: DateFilter): string | undefined => {
    if (filter === 'all') return undefined;
    const now = new Date();
    const days = filter === '7days' ? 7 : 30;
    const date = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);
    return date.toISOString();
  };

  // Load prompts
  useEffect(() => {
    async function loadPrompts() {
      try {
        setLoading(true);
        let url = `/api/leaderboard/unified-prompts?page=${currentPage}&limit=${PAGE_SIZE}`;
        if (sourceFilter !== 'all') url += `&source=${sourceFilter}`;
        if (voteFilter === 'voted') url += '&hasVotes=true';
        else if (voteFilter === 'unvoted') url += '&hasVotes=false';
        if (hideNoResults) url += '&hasResults=true';
        const createdAfter = getDateFilterValue(dateFilter);
        if (createdAfter)
          url += `&createdAfter=${encodeURIComponent(createdAfter)}`;
        if (promptSetFilter !== 'all') url += `&promptSetId=${promptSetFilter}`;
        if (categoryFilter !== 'all')
          url += `&category=${encodeURIComponent(categoryFilter)}`;

        const response = await apiRequest(url);
        setPrompts(response.prompts || response.seedPrompts || []);
        setPagination(response.pagination || null);
      } catch (err: any) {
        logError('Failed to load prompts', err);
        setError(err.message || 'Failed to load prompts');
      } finally {
        setLoading(false);
      }
    }

    loadPrompts();
  }, [
    currentPage,
    sourceFilter,
    voteFilter,
    hideNoResults,
    dateFilter,
    promptSetFilter,
    categoryFilter,
  ]);

  // Load prompt sets and categories on mount
  useEffect(() => {
    async function loadPromptSets() {
      try {
        const response = await apiRequest('/api/eval/prompt-sets');
        setPromptSets(response.promptSets || []);
      } catch (err: any) {
        logError('Failed to load prompt sets', err);
      }
    }

    loadPromptSets();
  }, []);

  // Filter change handlers
  const handleSourceFilterChange = (newFilter: SourceFilter) => {
    setSourceFilter(newFilter);
    setCurrentPage(1);
  };

  const handleVoteFilterChange = (newFilter: VoteFilter) => {
    setVoteFilter(newFilter);
    setCurrentPage(1);
  };

  const handleDateFilterChange = (newFilter: DateFilter) => {
    setDateFilter(newFilter);
    setCurrentPage(1);
  };

  const handlePromptSetFilterChange = (setId: string) => {
    setPromptSetFilter(setId);
    setCurrentPage(1);
  };

  const handleCategoryFilterChange = (category: CategoryFilter) => {
    setCategoryFilter(category);
    setCurrentPage(1);
  };

  const handleHideNoResultsToggle = () => {
    setHideNoResults(prev => !prev);
    setCurrentPage(1);
  };

  // Toggle featured status
  const handleToggleFeatured = async (
    promptId: string,
    currentValue: boolean
  ) => {
    try {
      await apiRequest(`/api/arena/runs/${promptId}/featured`, {
        method: 'PATCH',
        body: JSON.stringify({ featured: !currentValue }),
      });
      // Update local state
      setPrompts(prev =>
        prev.map(p =>
          p.id === promptId ? { ...p, featuredOnHome: !currentValue } : p
        )
      );
    } catch (err: any) {
      logError('Failed to toggle featured status', err);
    }
  };

  // Loading state
  if (loading && prompts.length === 0) {
    return (
      <EvalLayout>
        <div
          style={{
            textAlign: 'center',
            padding: '4rem',
            color: '#6B6B6B',
          }}
        >
          Loading prompts...
        </div>
      </EvalLayout>
    );
  }

  // Error state
  if (error) {
    return (
      <EvalLayout>
        <EmptyState
          title="Failed to load prompts"
          description={error}
          action={
            <Button onClick={() => window.location.reload()}>Retry</Button>
          }
        />
      </EvalLayout>
    );
  }

  // Stats
  const rankedCount = prompts.filter(p => p.totalVotes > 0).length;
  const totalVotesOnPage = prompts.reduce((sum, p) => sum + p.totalVotes, 0);
  const seedCount = prompts.filter(p => p.source === 'seed').length;
  const arenaCount = prompts.filter(p => p.source === 'arena').length;

  // Sort prompts: featured first, then by recency (already sorted by backend)
  const sortedPrompts = [...prompts].sort((a, b) => {
    // Featured prompts first
    if (a.featuredOnHome && !b.featuredOnHome) return -1;
    if (!a.featuredOnHome && b.featuredOnHome) return 1;
    return 0;
  });

  return (
    <EvalLayout>
      {/* Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: '2rem',
        }}
      >
        <div>
          <h1
            style={{
              fontSize: '2rem',
              fontWeight: 600,
              color: '#1A1A1A',
              margin: 0,
              marginBottom: '0.5rem',
            }}
          >
            Unified Prompts
          </h1>
          <p
            style={{
              fontSize: '0.9375rem',
              color: '#6B6B6B',
              margin: 0,
            }}
          >
            View all prompts from Arena and Seed sources, sorted by recency.
            Click a prompt to view results and rankings.
          </p>
        </div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '1rem',
            flexShrink: 0,
          }}
        >
          <VoteSearchBar onVoteFound={setLookedUpVote} />
          <Button onClick={() => setShowExecuteModal(true)}>
            Execute Prompts
          </Button>
        </div>
      </div>

      {/* Filters */}
      <FilterBar
        sourceFilter={sourceFilter}
        voteFilter={voteFilter}
        dateFilter={dateFilter}
        hideNoResults={hideNoResults}
        promptSetFilter={promptSetFilter}
        promptSets={promptSets}
        categoryFilter={categoryFilter}
        categories={CATEGORIES}
        onSourceFilterChange={handleSourceFilterChange}
        onVoteFilterChange={handleVoteFilterChange}
        onDateFilterChange={handleDateFilterChange}
        onHideNoResultsToggle={handleHideNoResultsToggle}
        onPromptSetFilterChange={handlePromptSetFilterChange}
        onCategoryFilterChange={handleCategoryFilterChange}
      />

      {/* Stats Grid */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
          gap: '1rem',
          marginBottom: '2rem',
        }}
      >
        <StatsCard
          label="Total Prompts"
          value={pagination?.total ?? prompts.length}
        />
        <StatsCard label="Seed" value={seedCount} color="#2E7D4A" />
        <StatsCard label="Arena" value={arenaCount} color="#1565C0" />
        <StatsCard label="Ranked" value={rankedCount} color="#8B2332" />
        <StatsCard label="Votes (page)" value={totalVotesOnPage} />
      </div>

      {/* Prompts Table */}
      <Table>
        <TableHead>
          <TableRow hover={false}>
            <TableHeaderCell align="center" width="50px">
              Home
            </TableHeaderCell>
            <TableHeaderCell align="center" width="70px">
              Source
            </TableHeaderCell>
            <TableHeaderCell>Prompt</TableHeaderCell>
            <TableHeaderCell width="150px">Category</TableHeaderCell>
            <TableHeaderCell align="center" width="100px">
              Status
            </TableHeaderCell>
            <TableHeaderCell align="center" width="100px">
              Results
            </TableHeaderCell>
            <TableHeaderCell align="center" width="80px">
              Votes
            </TableHeaderCell>
            <TableHeaderCell width="200px">Voters</TableHeaderCell>
            <TableHeaderCell align="right" width="100px">
              Last Result
            </TableHeaderCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedPrompts.length === 0 ? (
            <TableEmptyState message="No prompts found." colSpan={9} />
          ) : (
            sortedPrompts.map(prompt => (
              <TableRow key={prompt.id}>
                <TableCell align="center">
                  <button
                    onClick={() =>
                      handleToggleFeatured(prompt.id, prompt.featuredOnHome)
                    }
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: '4px',
                      borderRadius: '4px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                    title={
                      prompt.featuredOnHome
                        ? 'Remove from home page'
                        : 'Show on home page'
                    }
                  >
                    <StarIcon size={18} filled={prompt.featuredOnHome} />
                  </button>
                </TableCell>
                <TableCell align="center">
                  <SourceBadge source={prompt.source} size="sm" />
                </TableCell>
                <TableCell>
                  <Link
                    to={`/eval/unified/${prompt.id}`}
                    style={{
                      textDecoration: 'none',
                      color: '#1A1A1A',
                      display: 'block',
                    }}
                  >
                    <div
                      style={{
                        fontSize: '0.9375rem',
                        fontWeight: 500,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                    >
                      {prompt.promptText}
                    </div>
                  </Link>
                </TableCell>
                <TableCell>
                  {prompt.category ? (
                    <span
                      style={{
                        fontSize: '0.8125rem',
                        color: '#4A4A4A',
                        background: '#F5F0EB',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        display: 'inline-block',
                      }}
                    >
                      {prompt.category}
                    </span>
                  ) : (
                    <span style={{ color: '#A0A0A0', fontSize: '0.8125rem' }}>
                      —
                    </span>
                  )}
                </TableCell>
                <TableCell align="center">
                  <StatusBadge
                    variant={getStatusVariant(prompt.status)}
                    size="sm"
                  >
                    {prompt.status}
                  </StatusBadge>
                </TableCell>
                <TableCell align="center">
                  <span
                    style={{
                      fontWeight: 500,
                      color:
                        prompt.validResultsCount >= 3 ? '#2E7D4A' : '#B8860B',
                    }}
                  >
                    {prompt.validResultsCount}
                  </span>
                  <span style={{ color: '#6B6B6B' }}>
                    /{prompt.totalResultsCount}
                  </span>
                </TableCell>
                <TableCell align="center">
                  <span
                    style={{
                      fontWeight: 600,
                      color: prompt.totalVotes > 0 ? '#2E7D4A' : '#A0A0A0',
                    }}
                  >
                    {prompt.totalVotes}
                  </span>
                </TableCell>
                <TableCell>
                  <VoterBadges voters={prompt.voters} />
                </TableCell>
                <TableCell align="right">
                  <span style={{ fontSize: '0.8125rem', color: '#6B6B6B' }}>
                    {prompt.lastResultAt
                      ? formatDate(prompt.lastResultAt)
                      : '—'}
                  </span>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      {/* Pagination */}
      {pagination && (
        <PaginationControls
          pagination={pagination}
          currentPage={currentPage}
          onPageChange={setCurrentPage}
        />
      )}

      {/* Execute Modal */}
      {showExecuteModal && (
        <ExecuteFromSetModal
          onClose={() => setShowExecuteModal(false)}
          onSuccess={() => {
            window.location.reload();
          }}
        />
      )}

      {/* Vote Lookup Battle Modal */}
      {lookedUpVote && (
        <VoteComparisonModal
          vote={lookedUpVote}
          run={{
            prompt_text: lookedUpVote.prompt_text,
            prompt_id: lookedUpVote.prompt_id,
            category: lookedUpVote.category,
            source: lookedUpVote.source,
          }}
          onClose={() => setLookedUpVote(null)}
        />
      )}
    </EvalLayout>
  );
}
